import { useEffect } from 'react';
import ApexCharts from 'apexcharts';
import { getMonthsUpToCurrent } from '@/utils/date.utils';
import { CardContainer } from '../CardContainer';
import { TFunction } from 'i18next';
import { ChartLegend } from '@/components/Chart/ChartLegend';
import { InfoTip } from '@/components/Help/InfoTip';
import { DASHBOARD_TIPS } from '@/docs/tips/dashboard';

interface DashboardChartCardProps {
  t: TFunction<undefined>;
  createdVehiculeCheckData: number[];
  createdUsersData: number[];
}

export const DashboardChartCard = ({
  t,
  createdVehiculeCheckData,
  createdUsersData,
}: DashboardChartCardProps) => {
  useEffect(() => {
    const categories = getMonthsUpToCurrent(t);

    // Calculate the maximum value from both datasets
    const vehiculeChecksData = createdVehiculeCheckData || [];
    const usersData = createdUsersData || [];
    const allValues = [...vehiculeChecksData, ...usersData];
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;

    // Round up to the nearest 10th value
    const maxYStop = Math.ceil(maxValue / 10) * 10;

    const chart = new ApexCharts(
      document.querySelector('#dashboard-area-chart'),
      {
        series: [
          {
            name: t('common:inspections'),
            data: createdVehiculeCheckData,
          },
          {
            name: t('common:users'),
            data: createdUsersData,
          },
        ],
        chart: {
          type: 'area',
          height: 424,
          toolbar: {
            show: false,
          },
          background: 'transparent',
          zoom: {
            enabled: false,
          },
        },
        colors: ['#5433FF', '#20BDFF'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, maxYStop],
          },
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        markers: {
          size: 4,
          colors: ['#5433FF', '#20BDFF'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 6,
          },
        },
        grid: {
          show: true,
          borderColor: '#D9D9DD',
          strokeDashArray: 5,
          position: 'back',
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        xaxis: {
          categories: categories,
          // tickPlacement: 'between',
          tickPlacement: 'on',
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px',
            },
          },
        },
        yaxis: {
          min: 0,
          max: maxYStop,
          tickAmount: maxYStop / 10,
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px',
            },
            formatter: function (val: number) {
              return Math.round(val).toString();
            },
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (val: number) {
              return val.toString();
            },
          },
        },
      },
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [t, createdVehiculeCheckData, createdUsersData]);

  return (
    <CardContainer>
      <div id="dashboard-area-chart" />
      <div className="flex flex-1 justify-center items-center gap-[15px]">
        <ChartLegend
          text={t('common:inspections')}
          squareClassName="bg-purple-5433FF"
        />
        <ChartLegend
          text={t('common:users')}
          squareClassName="bg-blue-20BDFF"
        />
        <InfoTip tip={DASHBOARD_TIPS.monthlyChart} />
      </div>
    </CardContainer>
  );
};
